import { useState, useEffect } from "react";
import { Grid, FormControlLabel, Switch } from "@mui/material";
import InputGroup from "../../../../components/FormComponent/InputGroup";
import TextAreaGroup from "../../../../components/FormComponent/TextAreaGroup";
import SelectGroup from "../../../../components/FormComponent/SelectGroup";
import { httprequest } from "../../../../data/api";
import Button from "../../../../components/Button";
import { toast } from "react-toastify";
import useValidator from "../../../../hooks/useValidator";
import Layout from "../../../../components/Layout";
import BreadCrumb from "../../../../components/BreadCrumb";
import useStyles from "../../styles";
import { useUserState } from "../../../../context/UserContext";
import WidgetTitle from "../../../dashboard/components/WidgetTitle/WidgetTitle";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

const TEACHER_CLASS_ENDPOINT = "/api/v1/teacher/classes";
const ADMIN_CLASS_ENDPOINT = "/api/v1/class";
const ENROLMENT_EXAM_CONFIG_ENDPOINT = "/api/v1/enrolment-exam-configs";

const CreateEnrolmentExam = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { userRole } = useUserState();
  const [classData, setClassData] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [enrolExamValidator, showEnrolExamValidator] = useValidator();

  const [enrolExamForm, setEnrolExamForm] = useState({
    title: "",
    class_id: [],
    subject: "",
    description: "",
    instruction: "",
    duration: "",
    // Behavioural settings
    randomize_questions: "no",
    randomize_options: "no",
    questions_display_mode: "one_per_page",
    // Time settings
    start_date: "",
    end_date: "",
    max_time_per_question: "",
    // Proctoring / security settings
    proctoring_level: "",
    browser_lockdown: false,
    is_private: false
  });

  const handleAssignmentChange = (e) => {
    const { name, value, type, checked } = e.target;

    setEnrolExamForm({ ...enrolExamForm, [name]: type === "checkbox" ? checked : value });
  };

  /**
   * Builds the enrolment exam config payload (no questions).
   */
  const buildEnrolmentConfigPayload = (status = "published") => {
    const classIds = Array.isArray(enrolExamForm.class_id)
      ? enrolExamForm.class_id.map((c) => (typeof c === "object" && c?.class_id != null ? c.class_id : c)).filter(Boolean)
      : [];
    const durationNum = Number(enrolExamForm.duration);
    const maxTimeNum = enrolExamForm.max_time_per_question === "" ? null : Number(enrolExamForm.max_time_per_question);

    const payload = {
      title: (enrolExamForm.title || "").trim() || null,
      class_id: classIds,
      description: (enrolExamForm.description || "").trim() || "",
      instructions: (enrolExamForm.instruction || "").trim() || "",
      duration: Number.isNaN(durationNum) ? 0 : durationNum,
      subject: (enrolExamForm.subject || "").trim() || null,
      randomize_questions: enrolExamForm.randomize_questions === "yes",
      randomize_options: enrolExamForm.randomize_options === "yes",
      questions_display_mode: "one_per_page",
      start_date: enrolExamForm.start_date || null,
      end_date: enrolExamForm.end_date || null,
      max_time_per_question: Number.isNaN(maxTimeNum) ? null : maxTimeNum,
      is_private: !!enrolExamForm.is_private,
      status
    };

    if (enrolExamForm.proctoring_level) {
      payload.proctoring_requirements = { level: enrolExamForm.proctoring_level };
    }
    if (enrolExamForm.browser_lockdown) {
      payload.browser_lockdown_settings = {};
    }

    return payload;
  };

  const handleCreateEnrolmentExam = () => {
    if (enrolExamValidator.allValid()) {
      setIsCreating(true);
      const payload = buildEnrolmentConfigPayload("published");
      httprequest
        .post(ENROLMENT_EXAM_CONFIG_ENDPOINT, payload)
        .then((res) => {
          setIsCreating(false);
          toast.success(res?.data?.message ?? "Enrolment exam config created successfully.");
          const data = res?.data?.data;
          const configId = data?.id ?? res?.data?.id ?? null;
          if (configId) {
            navigate(`/enrolments/enrolment-examination-details/${configId}`);
          } else {
            navigate("/enrolments/enrolment-examination");
          }
        })
        .catch((err) => {
          setIsCreating(false);
          toast.error(err?.response?.data?.message ?? "Failed to create enrolment exam config.");
        });
    } else {
      showEnrolExamValidator(true);
    }
  };

  const getAllTeacherClasses = () => {
    httprequest
      .get(TEACHER_CLASS_ENDPOINT)
      .then((res) => {
        setClassData(res?.data?.data);
      })
      .catch(() => {});
  };

  // --| Get all classes as a school admin
  const getAllAdminClasses = () => {
    httprequest
      .get(ADMIN_CLASS_ENDPOINT)
      .then((res) => {
        setClassData(
          res?.data?.data.map((a_class) => ({
            class_id: a_class?.id,
            class_name: a_class?.class_name
          }))
        );
      })
      .catch(() => {});
  };

  // Handle class select
  const handleMultipleAccessChange = (selectedOptions) => {
    const selectedValues = selectedOptions.map((option) => option?.class_id);
    setEnrolExamForm({ ...enrolExamForm, class_id: selectedValues });
  };

  useEffect(() => {
    if (userRole?.role === "school_admin") {
      getAllAdminClasses();
    } else {
      getAllTeacherClasses();
    }
  }, []);

  return (
    <Layout
      children={
        <>
          <div className="breadcrumb-container">
            <div>
              <BreadCrumb
                link_one="/enrolments/enrolment-examination"
                link_one_name="All Enrolment Exam"
                active="Create An Enrolment Examination"
                description="Create a new Enrolment Examination"
              />
            </div>
            <div className="flex gap-10">
              {/* <Button
                variant="primaryoutline"
                buttonSize="fluid"
                color="btnfontprimary"
                isLoading={isDraftCreating}
                onClick={handleCreateDraftAssignment}
                buttonName="Save as Draft"
              /> */}
              <Button
                variant="primary"
                buttonSize="fluid"
                color="btndefault"
                buttonName="Create Enrolment Examination"
                isLoading={isCreating}
                onClick={handleCreateEnrolmentExam}
              />
            </div>
          </div>
          <div className="page-content text-left-important">
            <div className={styles.assignmentcontainerform}>
              <WidgetTitle
                title="Class Information"
                // subtitle="View Metrics for all student attendance in your school"
              />
              <div className={styles.assignmentsection}>
                <Grid container spacing={2}>
                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <div className="multiple-select">
                      <p>Class</p>
                      <Select isMulti name="class_id"
                        closeMenuOnSelect={true} options={classData} onChange={handleMultipleAccessChange}
                        getOptionLabel={(option) => option.class_name}
                        getOptionValue={(option) => option.class_id}
                      />
                    </div>
                    {enrolExamValidator.message("Class", enrolExamForm?.class_id, "required")}
                  </Grid>
                  <Grid item lg={4} md={4} sm={12} xs={12}>
                    <InputGroup
                      label="Subject"
                      margin="normal"
                      inputType="text"
                      inputName="subject"
                      value={enrolExamForm?.subject ?? ""}
                      onChange={handleAssignmentChange}
                      required
                    />
                    {enrolExamValidator.message(
                      "subject",
                      enrolExamForm?.subject,
                      "required"
                    )}
                  </Grid>
                  <Grid item lg={4} md={4} sm={12} xs={12}>
                    <InputGroup
                      label="Duration"
                      margin="normal"
                      inputType="number"
                      inputName="duration"
                      placeHolder="Set Duration in Minutes"
                      value={enrolExamForm?.duration ?? ""}
                      onChange={handleAssignmentChange}
                      required
                    />
                    {enrolExamValidator.message(
                      "duration",
                      enrolExamForm?.duration,
                      "required"
                    )}
                  </Grid>
                </Grid>
              </div>
              <WidgetTitle
                title="Enrolment Information"
              />
              <div className={styles.assignmentsection}>
                <Grid container spacing={2}>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <InputGroup
                      label="Enrolment Exam Title"
                      margin="normal"
                      inputType="text"
                      inputName="title"
                      value={enrolExamForm?.title ?? ""}
                      onChange={handleAssignmentChange}
                      required
                    />
                    {enrolExamValidator.message(
                      "title",
                      enrolExamForm?.title,
                      "required"
                    )}
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <div className={styles.inputmargin}>
                      <TextAreaGroup
                        label="Enrolment Exam Instruction"
                        inputName="instruction"
                        margin="normal"
                        placeholder=""
                        value={enrolExamForm?.instruction ?? ""}
                        onChange={handleAssignmentChange}
                      />
                    </div>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <div className={styles.inputmargin}>
                      <TextAreaGroup
                        label="Enrolment Exam Description"
                        inputName="description"
                        margin="normal"
                        placeholder=""
                        value={enrolExamForm?.description ?? ""}
                        onChange={handleAssignmentChange}
                      />
                    </div>
                  </Grid>
                </Grid>
              </div>
              <WidgetTitle title="Behavioural Settings" />
              <div className={styles.assignmentsection}>
                <Grid container spacing={2}>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectGroup
                      label="Randomize Questions"
                      children={
                        <select name="randomize_questions" value={enrolExamForm.randomize_questions} onChange={handleAssignmentChange}>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      }
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectGroup
                      label="Randomize Options"
                      children={
                        <select name="randomize_options" value={enrolExamForm.randomize_options} onChange={handleAssignmentChange}>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      }
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <label className={styles.enterque}>
                      <input
                        className="margin-top-5"
                        type="checkbox"
                        name="is_private"
                        checked={!!enrolExamForm.is_private}
                        onChange={handleAssignmentChange}
                      />
                      {" "}Visibility (Private)
                    </label>
                  </Grid>
                </Grid>
              </div>

              <WidgetTitle title="Time Settings" />
              <div className={styles.assignmentsection}>
                <Grid container spacing={2}>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <InputGroup
                      label="Duration (minutes)"
                      inputName="duration"
                      margin="normal"
                      inputType="number"
                      placeHolder="Set Duration in Minutes"
                      value={enrolExamForm?.duration ?? ""}
                      onChange={handleAssignmentChange}
                      required
                    />
                    {enrolExamValidator.message(
                      "duration",
                      enrolExamForm?.duration,
                      "required"
                    )}
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <InputGroup
                      label="Max time per question (optional, minutes)"
                      inputName="max_time_per_question"
                      margin="normal"
                      inputType="number"
                      placeHolder="e.g. 2"
                      value={enrolExamForm.max_time_per_question}
                      onChange={handleAssignmentChange}
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <InputGroup
                      label="Exam Start Date (optional)"
                      inputName="start_date"
                      margin="normal"
                      inputType="date"
                      value={enrolExamForm.start_date}
                      onChange={handleAssignmentChange}
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <InputGroup
                      label="Exam End Date (optional)"
                      inputName="end_date"
                      margin="normal"
                      inputType="date"
                      value={enrolExamForm.end_date}
                      onChange={handleAssignmentChange}
                    />
                  </Grid>
                </Grid>
              </div>

              <WidgetTitle title="Proctoring & Security" />
              <div className={styles.assignmentsection}>
                <Grid container spacing={2}>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectGroup
                      label="Proctoring level"
                      children={
                        <select
                          name="proctoring_level"
                          value={enrolExamForm.proctoring_level}
                          onChange={handleAssignmentChange}
                        >
                          <option value="">--- Select ---</option>
                          <option value="none">None</option>
                          <option value="basic">Basic</option>
                          <option value="strict">Strict</option>
                        </select>
                      }
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <FormControlLabel
                      control={(
                        <Switch
                          name="browser_lockdown"
                          checked={!!enrolExamForm.browser_lockdown}
                          onChange={handleAssignmentChange}
                          color="primary"
                        />
                      )}
                      label="Browser lockdown"
                    />
                  </Grid>
                </Grid>
              </div>
              {/* Questions are added on the exam details page after creation. */}
            </div>
          </div>
          {/* <ToastContainer /> */}
        </>
      }
    />
  );
};

export default CreateEnrolmentExam;
